// src/components/HomeSlider.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import useNavigateToArtwork from "@/hooks/useNavigateToArtwork";
import useProtectActionUnregistered from '@/hooks/useProtectActionUnregistered';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import HomeCarouselAnalyticsService from "@/services/homeCarouselAnalytics.service";
import {
  HOME_AUTO_INITIAL_ARTWORK_LIMIT,
} from "@/shared/constants/homeCarousel";
import ArtworkSliderCard from "@/shared-components/cards/components/ArtworkSliderCard";
import UnregisteredPopup from "@/shared-components/popups/unregistered-user-popup";
import FHSlider from "@/shared-components/sliders/components/FHSlider";
import Heading from "@/shared-components/text/components/Heading";
import Text from "@/shared-components/text/components/Text";
import { isRestrictedArtwork } from "@/utils/artworkVisibility";

import useCarouselImpressionTracking from "./hooks/useCarouselImpressionTracking";
import DiscoverHeaderControls from "./DiscoverHeaderControls";

import "./styles/HomeSlider.css";

const getArtworkKey = (item) =>
  item?._id || item?.id || item?.slug || null;

function normalizeArtworkForSlider(item) {
  if (!item || typeof item !== "object") {
    return null;
  }
  if (!item.slug || isRestrictedArtwork(item)) {
    return null;
  }

  const mainPicture = item?.artwork_media?.artwork_main_picture;
  if (!mainPicture?.image_id) {
    return null;
  }

  const rawAspectRatio = mainPicture?.image_original_data?.aspect_ratio;
  const hasValidAspectRatio = Number.isFinite(rawAspectRatio) && rawAspectRatio > 0;

  if (hasValidAspectRatio) {
    return item;
  }

  return {
    ...item,
    artwork_media: {
      ...(item.artwork_media || {}),
      artwork_main_picture: {
        ...mainPicture,
        image_original_data: {
          ...(mainPicture.image_original_data || {}),
          aspect_ratio: 1,
        },
      },
    },
  };
}

export default function HomeSlider({
  artworks,
  alternativeArtworks,
  children,
  category,
  endpoint,
  total,
  link,
  contentMode = "manual",
  hideText = false,
  trackingContext = {},
}) {
  const tHome = useTranslations('common.shared.home');
  const { state } = useStateHandler();
  const { privateActionHandlerUnregistered, showPopup } = useProtectActionUnregistered({ typeOfProtection: "USER_LIMIT_VIEW" });

  function handleClick(event) {
    privateActionHandlerUnregistered(event);
    return event?.defaultPrevented;
  }

  // Share the same click logic for classic and auction banners
  const sliderRef = useRef(null);
  const impressionRef = useCarouselImpressionTracking(trackingContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const isAutomaticMode = String(contentMode || "manual").toLowerCase() === "automatic";
  const parsedTotal = Number(total);
  const baseArtworks = useMemo(
    () =>
      (alternativeArtworks || artworks || [])
        .map(normalizeArtworkForSlider)
        .filter(Boolean),
    [alternativeArtworks, artworks]
  );
  const displayPool = baseArtworks;
  const displayedArtworks = displayPool.slice(0, HOME_AUTO_INITIAL_ARTWORK_LIMIT);
  const manualTotal = Number.isFinite(parsedTotal) && parsedTotal > 0
    ? parsedTotal
    : baseArtworks.length;
  const showSeeAll = !isAutomaticMode && manualTotal > HOME_AUTO_INITIAL_ARTWORK_LIMIT;
  const artworkSourceMeta = isAutomaticMode
    ? {
      type: "collection-page",
      label: category,
    }
    : { type: "home-carousel", label: category };

  const navigateToArtwork = useNavigateToArtwork(
    `${category}_collection_page`,
    {
      totalDocs: manualTotal,
      data: displayPool,
    },
    endpoint,
    artworkSourceMeta
  );

  // Controls are centralized via DiscoverHeaderControls

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      setIsScrolled(slider.scrollLeft > 0);
    };

    slider.addEventListener("scroll", handleScroll);
    return () => slider.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {showPopup && <UnregisteredPopup showPopUp={showPopup} />}
      <div ref={impressionRef} className="home-slider__container element-non-selectable">
        <div className="home-slider__header">
          <Heading className="home-title">{category}</Heading>
          <DiscoverHeaderControls viewportRef={sliderRef} />
        </div>

        <div className="full-bleed">
          <FHSlider
            ref={sliderRef}
            speed={1.4}
            className={`home-slider__slider ${isScrolled ? "scrolled" : ""}`}
            /* Hide internal controls in Discover; using header controls */
            customPrevButton={<></>}
            customNextButton={<></>}
          >
            {children}

            {displayedArtworks.map((item, index) => (
              <ArtworkSliderCard
                key={`${category}${getArtworkKey(item) || item.slug || index}`}
                artworkData={item}
                imageClassName="home-slider__slider-item-image"
                cardClassName="home-slider__slider-item always-show-text"
                hideText={hideText}
                onClick={(event) => {
                  if (!state.user_session && handleClick(event)) {
                    return;
                  }
                  HomeCarouselAnalyticsService.trackHomeCarouselEvent(
                    "carousel_item_click",
                    {
                      ...trackingContext,
                      itemId: item._id || item.id || item.slug,
                      itemKind: "artwork",
                      destination: item.slug ? `/artwork/${item.slug}` : null,
                    },
                    { userSession: state.user_session }
                  );
                  navigateToArtwork(item.slug);
                }}
              />
            ))}

            {showSeeAll ? (
              <div className="home-slider__slider-item see-all-slide">
                <Link
                  className="no-underline"
                  to={`/collections/${link}`}
                  state={{
                    from: true,
                    collectionName: category,
                    preferredArtworkOrder: displayedArtworks
                      .map((artwork) => getArtworkKey(artwork))
                      .filter(Boolean),
                  }}
                >
                  <Text
                    className="home-slider__see-all font-size-medium"
                    onClick={(event) => {
                      if (!state.user_session && handleClick(event)) {
                        return;
                      }
                      HomeCarouselAnalyticsService.trackHomeCarouselEvent(
                        "carousel_see_all_click",
                        {
                          ...trackingContext,
                          destination: `/collections/${link}`,
                        },
                        { userSession: state.user_session }
                      );
                    }}>
                    {tHome('see_all')}
                  </Text>
                </Link>
              </div>
            ) : null}
          </FHSlider>
        </div>
      </div>
    </>
  );
}
